import React from 'react'
import { LoaderBar, PaginationComponent } from 'components'
import { DesktopMode, MobileMode } from 'layout'
import styled from 'styled-components'
import { Card } from 'semantic-ui-react'
import { DateUtility, DateFormat } from 'utility'
import { ENABLE_LICENSE_SHOPPING_CART } from 'utility/subscription-usage'

const Frame = styled.div`
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
    overflow-x: auto;
`

const Grid = styled.table`
    width: 100%;
    min-width: 860px;
    border-collapse: collapse;
    table-layout: fixed;

    th {
        text-align: left;
        padding: 12px 16px;
        background: #fafaf9;
        color: #78716c;
        font-size: 12px;
        font-weight: 600;
        border-bottom: 1px solid #e7e5e4;
    }

    td {
        padding: 13px 16px;
        border-bottom: 1px solid #f5f5f4;
        color: #1c1917;
        font-size: 14px;
        vertical-align: middle;
    }

    tr:last-child td {
        border-bottom: 0;
    }

    tbody tr:hover td {
        background: #fafaf9;
    }

    th:nth-child(1),
    td:nth-child(1) {
        width: 18%;
    }

    th:nth-child(2),
    td:nth-child(2) {
        width: 14%;
    }

    th:nth-child(5),
    td:nth-child(5) {
        width: 18%;
    }

    th:last-child,
    td:last-child {
        width: 168px;
        text-align: right;
        padding-right: 14px;
        white-space: nowrap;
    }
`

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    white-space: nowrap;
`

const DeactivateButton = styled.button`
    height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid #e7e5e4;
    background: #fff;
    color: #44403c;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: #fafaf9;
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`

const Person = styled.span`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    font-weight: 600;
`

const Avatar = styled.span`
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #e7eeff;
    color: #1d4ed8;
    display: inline-grid;
    place-items: center;
    font-size: 12px;
    font-weight: 700;
    flex: none;
`

const Clip = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #57534e;
    min-width: 0;
`

const IconButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e7e5e4;
    background: #fff;
    color: #1e3a8a;
    display: inline-grid;
    place-items: center;
    cursor: pointer;
    padding: 0;

    svg {
        width: 15px;
        height: 15px;
    }

    &:hover {
        background: #e7eeff;
    }
`

const Empty = styled.div`
    padding: 36px 16px;
    text-align: center;
    color: #78716c;
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
`

const Pager = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
`

const formatDateTime = dateTimeString => {
    if (!dateTimeString) return '—'
    try {
        const date = new Date(dateTimeString)
        return DateUtility.dateToLocalISO(date, DateFormat.dateTime)
    } catch {
        return dateTimeString
    }
}

const shown = value => {
    const raw = value == null ? '' : String(value).trim()
    return raw || '—'
}

const initials = name => {
    const clean = shown(name)
    if (clean === '—') return '?'
    const parts = clean.split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}

const isActiveMember = item => item?.IsActive !== false && item?.IsActive !== 0

const Pencil = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 20h4l10.2-10.2a1.8 1.8 0 0 0 0-2.6l-1.4-1.4a1.8 1.8 0 0 0-2.6 0L4 16v4z" />
        <path d="M13.2 6.8l4 4" />
    </svg>
)

const UserCard = ({ item, onEditUser, onDeactivate }) => (
    <Card fluid>
        <Card.Content>
            <Card.Header className="card-title">{item.Name}</Card.Header>
            <Card.Meta>User ID: {item.UserId}</Card.Meta>
            <Card.Meta>Phone Number: {item.PhoneNo}</Card.Meta>
            <Card.Description>Email: {item.Email}</Card.Description>
            {item.LastLoginDateTime && (
                <Card.Meta>Last Login: {formatDateTime(item.LastLoginDateTime)}</Card.Meta>
            )}
            <Actions className="mt-2">
                {ENABLE_LICENSE_SHOPPING_CART && isActiveMember(item) && (
                    <DeactivateButton type="button" onClick={() => onDeactivate(item)}>
                        Deactivate
                    </DeactivateButton>
                )}
                <IconButton
                    type="button"
                    aria-label={`Edit ${shown(item.Name)}`}
                    onClick={() => onEditUser(item)}
                >
                    <Pencil />
                </IconButton>
            </Actions>
        </Card.Content>
    </Card>
)

const DesktopTable = ({ people, onEditUser, onDeactivate }) => (
    <Frame>
        <Grid>
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Phone Number</th>
                    <th>User ID</th>
                    <th>Email</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {people.map(item => (
                    <tr key={item.unique_id || item.UserId || item.Email}>
                        <td>
                            <Person>
                                <Avatar>{initials(item.Name)}</Avatar>
                                <Clip style={{ color: '#1c1917', fontWeight: 600, flex: 1 }}>{shown(item.Name)}</Clip>
                            </Person>
                        </td>
                        <td><Clip>{shown(item.PhoneNo)}</Clip></td>
                        <td><Clip title={shown(item.UserId)}>{shown(item.UserId)}</Clip></td>
                        <td><Clip title={shown(item.Email)}>{shown(item.Email)}</Clip></td>
                        <td><Clip>{formatDateTime(item.LastLoginDateTime)}</Clip></td>
                        <td>
                            <Actions>
                                {ENABLE_LICENSE_SHOPPING_CART && isActiveMember(item) && (
                                    <DeactivateButton type="button" onClick={() => onDeactivate(item)}>
                                        Deactivate
                                    </DeactivateButton>
                                )}
                                <IconButton
                                    type="button"
                                    aria-label={`Edit ${shown(item.Name)}`}
                                    onClick={() => onEditUser(item)}
                                >
                                    <Pencil />
                                </IconButton>
                            </Actions>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Grid>
    </Frame>
)

export const UserList = ({
    data,
    loading,
    filter,
    pageChanged,
    totalCount,
    onEditUser,
    onDeactivate,
}) => {
    const people = Array.isArray(data) ? data : []

    return (
        <div>
            {loading && <LoaderBar content="Loading" />}
            {!loading && people.length === 0 && <Empty>No users yet.</Empty>}
            {people.length > 0 && (
                <>
                    <DesktopMode>
                        <DesktopTable
                            people={people}
                            onEditUser={onEditUser}
                            onDeactivate={onDeactivate}
                        />
                    </DesktopMode>
                    <MobileMode>
                        {people.map(item => (
                            <div key={item.unique_id || item.UserId || item.Email} className="mb-3">
                                <UserCard
                                    item={item}
                                    onEditUser={onEditUser}
                                    onDeactivate={onDeactivate}
                                />
                            </div>
                        ))}
                    </MobileMode>
                </>
            )}
            {people.length > 0 && (
                <Pager>
                    <PaginationComponent
                        activePage={filter?.PageNumber}
                        pageChanged={pageChanged}
                        pageSize={filter?.PageSize}
                        totalCount={totalCount}
                    />
                </Pager>
            )}
        </div>
    )
}
